import React, { useState, useMemo } from "react";
import Refuse from "./refuseFromPolice";
import ApproveFromPoliceComplaint from "./approveFromPolice";

function ComplaintDetailsModal({
  selectedReport,
  complaintDescription,
  onActionCompleted,
  complaintStatus,
  adminName
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoomedImage, setZoomedImage] = useState(null);

  // const adminName = localStorage.getItem("adminName") || ""; 

  const isPending =
    complaintStatus === 0 ||
    complaintStatus === "Pending" ||
    complaintStatus === "Në pritje";

  const formattedDate = useMemo(() => {
    if (!selectedReport?.date) return "";
    return new Date(selectedReport.date).toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [selectedReport?.date]);

  const combinedMedia = useMemo(
    () => [
      ...(selectedReport?.photos || []),
      ...(selectedReport?.videos || []),
    ],
    [selectedReport?.photos, selectedReport?.videos]
  );

  const isVideo = (media) => {
    if (!media || typeof media !== "string") return false;
    const u = media.split("?")[0].toLowerCase();
    return [".mp4", ".avi", ".mov", ".webm", ".mkv"].some((ext) =>
      u.endsWith(ext)
    );
  };

  const prevSlide = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + combinedMedia.length) % combinedMedia.length
    );
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % combinedMedia.length);
  };

  const prevZoomed = () => {
    const newIndex =
      (currentIndex - 1 + combinedMedia.length) % combinedMedia.length;
    setCurrentIndex(newIndex);
    if (!isVideo(combinedMedia[newIndex]))
      setZoomedImage(combinedMedia[newIndex]);
  };

  const nextZoomed = () => {
    const newIndex = (currentIndex + 1) % combinedMedia.length;
    setCurrentIndex(newIndex);
    if (!isVideo(combinedMedia[newIndex]))
      setZoomedImage(combinedMedia[newIndex]);
  };

  return (
    <>
      <input type="checkbox" id="my-modal-5" className="modal-toggle" />
      <div className="modal">
        <div className="modal-box w-11/12 max-w-5xl rounded-2xl bg-white p-6 shadow-xl">
          {/* Header */}
          <div className="mb-4 flex items-center justify-between">
            <h3 className="w-full text-center text-lg font-bold text-brand-500">
              Detajet e raportit dhe ankesës
            </h3>
            <label
              htmlFor="my-modal-5"
              className="btn btn-circle btn-sm bg-white text-brand-700 hover:text-white"
              onClick={() => setZoomedImage(null)}
            >
              ✕
            </label>
          </div>
          <hr />

          {/* Report Info */}
          <div className="mt-4 space-y-3">
            <p>
              <span className="font-bold">Qyteti:</span>{" "}
              {selectedReport?.address
                ? (() => {
                    const gpsMatch =
                      selectedReport.address.match(/\(([^)]+)\)/);
                    const cityName = selectedReport.address
                      .split("(")[0]
                      .trim();

                    if (gpsMatch && gpsMatch[1]) {
                      const [lat, lng] = gpsMatch[1]
                        .split(",")
                        .map((x) => x.trim());
                      const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;

                      return (
                        <>
                          <span>{cityName} </span>
                          <span>(</span>
                          <a
                            href={googleMapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cursor-pointer font-semibold text-blue-600 underline"
                            title="Shiko lokacionin në Google Maps"
                          >
                            {lat}, {lng}
                          </a>
                          <span>)</span>
                        </>
                      );
                    }
                    return <span>{selectedReport?.address}</span>;
                  })()
                : "Unknown"}
            </p>

            <p>
              <b>Kategoria:</b> {selectedReport?.category}
            </p>
            <p>
              <b>Data:</b> {formattedDate}
            </p>
            <p className="whitespace-pre-wrap break-words">
              <b>Përshkrimi:</b> {selectedReport?.textContent}
            </p>
            <p className="whitespace-pre-wrap break-words">
              <b>Ankesa:</b> {complaintDescription}
            </p>
          </div>

          {/* Media Carousel */}
          {combinedMedia.length > 0 && (
            <div className="relative mx-auto mt-6 flex max-w-2xl items-center justify-center">
              <div className="relative">
                {isVideo(combinedMedia[currentIndex]) ? (
                  <video
                    src={combinedMedia[currentIndex]}
                    className="max-h-80 w-auto max-w-full rounded-lg object-contain shadow"
                    controls
                  />
                ) : (
                  <img
                    src={combinedMedia[currentIndex]}
                    alt="Complaint media"
                    className="max-h-80 w-auto max-w-full cursor-pointer rounded-lg object-contain shadow transition hover:scale-105"
                    onClick={() => setZoomedImage(combinedMedia[currentIndex])}
                  />
                )}

                {/* ✅ Watermark */}
                {adminName?.trim() && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center select-none">
                    <span
                      className="font-bold text-white/30 whitespace-normal text-center break-words"
                      style={{
                        transform: "rotate(-45deg)",
                        userSelect: "none",
                        width: "90%",
                        fontSize: "clamp(14px, 4vw, 40px)",
                        lineHeight: "1.1",
                      }}
                    >
                      {adminName}
                    </span>
                  </div>
                )}
              </div>

              {combinedMedia.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="btn btn-circle absolute left-2 bg-white text-brand-700 shadow-md hover:text-white"
                  >
                    ❮
                  </button>
                  <button
                    onClick={nextSlide}
                    className="btn btn-circle absolute right-2 bg-white text-brand-700 shadow-md hover:text-white"
                  >
                    ❯
                  </button>
                </>
              )}
            </div>
          )}

          {/* Footer */}
          <footer className="modal-action mt-6 flex justify-end gap-2">
            <label
              htmlFor="my-modal-5"
              className="btn bg-white text-brand-700 hover:text-white"
            >
              Kthehu
            </label>
            {isPending && (
              <>
                <label
                  htmlFor="my-modal-6"
                  className="btn bg-red-500 text-white hover:bg-red-600"
                >
                  Refuzo
                </label>
                <label
                  htmlFor="my-modal-8"
                  className="btn bg-brand-500 text-white hover:bg-brand-700"
                >
                  Aprovo
                </label>
                <Refuse
                  id={selectedReport?.complaintId}
                  onSuccess={() => {
                    document.getElementById("my-modal-5").checked = false;
                    onActionCompleted?.(selectedReport?.complaintId, 2);
                  }}
                />
                <ApproveFromPoliceComplaint
                  id={selectedReport?.complaintId}
                  onSuccess={() => {
                    document.getElementById("my-modal-5").checked = false;
                    onActionCompleted?.(selectedReport?.complaintId, 1);
                  }}
                />
              </>
            )}
          </footer>
        </div>
      </div>

      {/* Fullscreen Image Viewer */}
      {zoomedImage && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-90">
          <button
            className="absolute right-4 top-4 text-3xl text-white"
            onClick={() => setZoomedImage(null)}
          >
            ✕
          </button>
          {combinedMedia.length > 1 && (
            <>
              <button
                onClick={prevZoomed}
                className="absolute left-5 text-4xl text-white"
              >
                ❮
              </button>
              <button
                onClick={nextZoomed}
                className="absolute right-5 text-4xl text-white"
              >
                ❯
              </button>
            </>
          )}
          <div className="relative">
            <img
              src={zoomedImage}
              alt="Zoomed complaint media"
              className="max-h-full max-w-full rounded-lg object-contain transition-transform duration-200 hover:scale-110"
            />
            {adminName?.trim() && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center select-none">
                <span
                  className="font-bold text-white/30 whitespace-normal text-center break-words"
                  style={{
                    transform: "rotate(-45deg)",
                    userSelect: "none",
                    width: "90%",
                    fontSize: "clamp(14px, 4vw, 40px)",
                    lineHeight: "1.1",
                  }}
                >
                  {adminName}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default ComplaintDetailsModal;
